import { supabase } from "../supabase";

/**
 * Optional Material catalog image: ONE optimized JPEG per material.
 *
 * Storage: private bucket `material-images`, stable object path
 *   {organization_id}/{material_id}/image.jpg
 * referenced by `materials.image_path` (NULL = no image, no object).
 * Replace = upsert on the same path (never an orphan); Remove = delete that
 * one object. This is a catalog image, not a Project Picture: we never store
 * the phone's original, only the optimized image.
 */

export const MATERIAL_IMAGES_BUCKET = "material-images";
export const MATERIAL_IMAGE_MIME = "image/jpeg";
export const MATERIAL_IMAGE_SIGNED_SECONDS = 60 * 60;

export const MATERIAL_IMAGE_OPTIMIZATION = Object.freeze({
  /** Longest edge in px. Never upscales. */
  MAX_EDGE: 1024,
  /** JPEG quality ladder; stop at the first result under TARGET_BYTES. */
  QUALITIES: [0.82, 0.74, 0.66, 0.58],
  TARGET_BYTES: 200 * 1024,
  /** Matches the bucket's file_size_limit. */
  HARD_MAX_BYTES: 1024 * 1024,
  /** Refuse absurd inputs before decoding. */
  MAX_INPUT_BYTES: 40 * 1024 * 1024,
});

export function materialImagePath(organizationId, materialId) {
  return `${organizationId}/${materialId}/image.jpg`;
}

/** Fit inside MAX_EDGE, preserve aspect ratio, never upscale. */
export function computeTargetSize(width, height, maxEdge = MATERIAL_IMAGE_OPTIMIZATION.MAX_EDGE) {
  const w = Math.max(1, Math.round(Number(width) || 1));
  const h = Math.max(1, Math.round(Number(height) || 1));
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/**
 * Decode with EXIF orientation applied (phone photos are often stored
 * sideways). createImageBitmap honours `imageOrientation: "from-image"`;
 * elsewhere an <img> is auto-oriented by the browser.
 */
export async function decodeImageFile(file) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.(),
      };
    } catch {
      // fall through to <img>
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("decode"));
      element.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function defaultCreateCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), MATERIAL_IMAGE_MIME, quality);
  });
}

/**
 * Resize + compress a chosen/taken photo into the single stored catalog image.
 * Dependencies are injectable so the pipeline is testable without a browser.
 *
 * @returns {Promise<{ blob: Blob, width: number, height: number, bytes: number, quality: number }>}
 */
export async function optimizeMaterialImage(
  file,
  { decode = decodeImageFile, createCanvas = defaultCreateCanvas, toBlob = canvasToJpegBlob } = {},
) {
  if (!file) {
    throw new Error("No photo selected.");
  }
  if (file.type && !/^image\//i.test(file.type)) {
    throw new Error("Choose an image file.");
  }
  if (file.size > MATERIAL_IMAGE_OPTIMIZATION.MAX_INPUT_BYTES) {
    throw new Error("That photo is too large. Choose a smaller one.");
  }

  let decoded;
  try {
    decoded = await decode(file);
  } catch {
    throw new Error("This photo format can't be read here. Try a JPEG or PNG photo.");
  }

  try {
    const { width, height } = computeTargetSize(decoded.width, decoded.height);
    const canvas = createCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to prepare the photo.");
    }
    // JPEG has no alpha: flatten transparent PNGs onto white, not black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(decoded.source, 0, 0, width, height);

    let best = null;
    for (const quality of MATERIAL_IMAGE_OPTIMIZATION.QUALITIES) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await toBlob(canvas, quality);
      if (!blob) {
        throw new Error("Unable to compress the photo.");
      }
      best = { blob, quality };
      if (blob.size <= MATERIAL_IMAGE_OPTIMIZATION.TARGET_BYTES) break;
    }

    if (best.blob.size > MATERIAL_IMAGE_OPTIMIZATION.HARD_MAX_BYTES) {
      throw new Error("That photo is still too large after compression. Try another one.");
    }

    return { blob: best.blob, width, height, bytes: best.blob.size, quality: best.quality };
  } finally {
    decoded.release?.();
  }
}

/** True when the database/storage for material images is not set up yet. */
export function isMissingImageSupportError(error) {
  const text = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`.toLowerCase();
  return (
    error?.code === "42703" ||
    error?.code === "PGRST204" ||
    (text.includes("image_path") && /column|schema|exist|find/.test(text))
  );
}

export function isMissingBucketError(error) {
  const text = `${error?.message || ""}`.toLowerCase();
  return text.includes("bucket not found") || error?.statusCode === "404" || error?.status === 404;
}

/** Upload (or replace, via upsert on the stable path) the single image. */
export async function uploadMaterialImage({
  organizationId,
  materialId,
  blob,
  client = supabase,
}) {
  if (!organizationId || !materialId) {
    throw new Error("Organization and material are required to save a photo.");
  }
  const path = materialImagePath(organizationId, materialId);
  const { error } = await client.storage.from(MATERIAL_IMAGES_BUCKET).upload(path, blob, {
    upsert: true,
    contentType: MATERIAL_IMAGE_MIME,
    cacheControl: "3600",
  });
  if (error) throw error;
  return path;
}

/** Delete the single object. Returns { error } rather than throwing. */
export async function removeMaterialImage({ organizationId, materialId, client = supabase }) {
  if (!organizationId || !materialId) return { error: null };
  const path = materialImagePath(organizationId, materialId);
  try {
    const { error } = await client.storage.from(MATERIAL_IMAGES_BUCKET).remove([path]);
    return { error: error || null };
  } catch (error) {
    return { error };
  }
}

/** Batch-sign read URLs for the private bucket. Failures yield no URLs (placeholder). */
export async function signMaterialImagePaths(paths, client = supabase) {
  const unique = [...new Set((paths || []).filter(Boolean))];
  if (!unique.length) return {};
  try {
    const { data, error } = await client.storage
      .from(MATERIAL_IMAGES_BUCKET)
      .createSignedUrls(unique, MATERIAL_IMAGE_SIGNED_SECONDS);
    if (error) return {};
    const byPath = {};
    (data || []).forEach((item) => {
      if (item?.path && item?.signedUrl) byPath[item.path] = item.signedUrl;
    });
    return byPath;
  } catch {
    return {};
  }
}
